/**
 * AppShell - Main application layout component
 *
 * Provides the overall structure with header, sidebar, and content area
 */

import { ForgeScaffold } from '@tylertech/forge-react';
import '@tylertech/forge/scaffold';
import Toolbar from './Toolbar';
import Sidebar from './Sidebar';
import { useOktaAuth } from '../../hooks/useOktaAuth';
import { usePreferencesSync } from '../../hooks/usePreferencesSync';
import { useAppSelector } from '../../store';
import { selectIsAuthenticated } from '../../store/features/auth/authSlice';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import './AppShell.css';

export interface AppShellProps {
  children: React.ReactNode;
}

export default function AppShell({ children }: AppShellProps): JSX.Element {
  const { isLoading } = useOktaAuth();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);

  // Load + apply the user's saved theme/locale/timezone preferences (issue #424).
  usePreferencesSync();

  // Hold spinner while loading AND not yet authenticated.
  // isLoading stays true during the callback exchange (redirecting=true in useOktaAuth),
  // but once the callback dispatches setUser/setToken, isAuthenticated flips true
  // and we can release the gate even before isLoading clears.
  if (isLoading && !isAuthenticated) {
    return <LoadingSpinner centered message="Authenticating..." />;
  }

  return (
    <div className="app-shell">
      <ForgeScaffold>
        <Toolbar slot="header" />
        <Sidebar slot="body-left" />
        <main className="app-content" slot="body" tabIndex={0} aria-label="Main content">
          {children}
        </main>
      </ForgeScaffold>
    </div>
  );
}
